
import React from 'react';
import { OnboarderTemplateData } from './OnboarderTemplate';
import '../themes/cryptoramos-premium.css';

// Reusing components from OnboarderTemplate to maintain consistency? 
// Or better to import them if exported?
// For now, I'll redefine small local components to keep this self-contained and "clean/well-abstracted" as requested,
// relying on the global CSS for the theme.

const Navbar: React.FC<{ name: string }> = ({ name }) => (
    <nav className="navbar">
        <div className="container nav-content">
            <div className="logo">
                <img
                    src="https://cryptoramos.com/wp-content/uploads/2026/01/logo.png"
                    alt="CryptoRamos"
                    className="logo-img"
                />
                <span>{name}</span>
            </div>
            <div className="nav-links">
                <a href="/">Home</a>
                <a href="/#community">Community</a>
                <a href="/#features">Features</a>
            </div>
            <a href="/" className="nav-cta">
                Back to Main Site
            </a>
        </div>
    </nav>
);

const StepCard: React.FC<{
    number: string;
    title: string;
    subtitle: string;
    children: React.ReactNode
}> = ({ number, title, subtitle, children }) => (
    <div className="roadmap-step">
        <div className="step-marker">
            <div className="step-number">{number}</div>
            <div className="step-line"></div>
        </div>
        <div className="step-content">
            <h2 className="step-title">{title}</h2>
            <p className="step-subtitle">{subtitle}</p>
            <div className="step-body glass-card">
                <div className="step-body-inner">
                    {children}
                </div>
            </div>
        </div>
    </div>
);

const VideoEmbed: React.FC<{ videoId: string; title: string }> = ({ videoId, title }) => (
    <div className="video-container">
        <iframe
            width="100%"
            height="100%"
            src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ border: 0, borderRadius: '12px' }}
        ></iframe>
    </div>
);

const ExchangeCard: React.FC<{
    name: string;
    offer: string;
    link: string;
    bgColor: string;
}> = ({ name, offer, link, bgColor }) => (
    <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="exchange-card"
        style={{ '--hover-color': bgColor } as React.CSSProperties}
    >
        <div className="exchange-info">
            <h3>{name}</h3>
            <span>{offer}</span>
        </div>
        <div className="exchange-arrow">→</div>
    </a>
);

export const StartHereTemplate: React.FC<{ data: OnboarderTemplateData }> = ({ data }) => {
    const { identity } = data;

    return (
        <div className="app roadmap-page">
            <div className="bg-grid"></div>
            <div className="bg-radial"></div>

            <Navbar name={identity.name} />

            <main className="roadmap-container container">
                <header className="roadmap-header">
                    <h1 className="hero-title">The <span className="text-gradient">Blueprint</span></h1>
                    <p className="hero-subline">
                        Your exact step-by-step roadmap to becoming a profitable crypto trader.
                        No fluff, just the strategy.
                    </p>
                </header>

                <div className="roadmap-timeline">
                    {/* Step 1: The Intro */}
                    <StepCard
                        number="01"
                        title="Welcome to the Team"
                        subtitle="Watch this first to understand the mission and mindset."
                    >
                        <VideoEmbed videoId="yBKXj9NC8cU" title="Welcome to Team Ramos" />
                    </StepCard>

                    {/* Step 2: The Tools */}
                    <StepCard
                        number="02"
                        title="The Arsenal"
                        subtitle="Set up your trading accounts to follow my exact setups."
                    >
                        <div className="exchanges-grid">
                            <ExchangeCard
                                name="BTCC"
                                offer="$40,000+ in Rewards"
                                link="https://www.btcc.com/market-promotion/bonus2/kol?name=Oscar#signUp"
                                bgColor="#3B82F6"
                            />
                            <ExchangeCard
                                name="Weex"
                                offer="Low Fees & High Liquidity"
                                link="https://www.weex.com/register?vipCode=xz3u"
                                bgColor="#F59E0B"
                            />
                            <ExchangeCard
                                name="Bitunix"
                                offer="Fast Execution"
                                link="https://www.bitunix.com/register?vipCode=O5qQ"
                                bgColor="#10B981"
                            />
                        </div>
                    </StepCard>

                    {/* Step 3: The Strategy */}
                    <StepCard
                        number="03"
                        title="The Strategy"
                        subtitle="How I open trades, manage risk, and take massive profits."
                    >
                        <VideoEmbed videoId="kcIzn81pnIc" title="Crypto Leverage Guide" />
                    </StepCard>

                    {/* Step 4: The Community */}
                    <StepCard
                        number="04"
                        title="Join the Army"
                        subtitle="Never trade alone. Join 166K+ members in our private channels."
                    >
                        <div className="community-buttons">
                            <a href={data.social.telegram || "#"} target="_blank" rel="noopener noreferrer" className="btn btn-primary large telegram-btn">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px' }}>
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" />
                                </svg>
                                Join Telegram
                            </a>
                            <a href={data.social.discord || "#"} target="_blank" rel="noopener noreferrer" className="btn btn-secondary large discord-btn">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px' }}>
                                    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2763-3.68-.2763-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.419-2.1568 2.419zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.419-2.1568 2.419z" />
                                </svg>
                                Join Discord
                            </a>
                        </div>
                    </StepCard>
                </div>

                <footer className="roadmap-footer">
                    <p>© {new Date().getFullYear()} Oscar Ramos</p>
                </footer>
            </main>
        </div>
    );
};

export default StartHereTemplate;
